/**
 * AffiliationEditor — inline form for editing OR adding an affiliation
 * (Block 6.B.3 / ADR-024 D-affiliation-editor-shape +
 * D-set-primary-affordance + D-date-input).
 *
 * Per ADR-024 §"Per-form Save exceptions" (T1-absorbed principle): per-
 * form Save earns its place when ALL THREE hold: (1) multi-field
 * coherence, (2) inter-field validation, (3) undo-stack-as-one-logical-
 * unit. Affiliation editor satisfies all three; per-form Save is the
 * right grain rather than auto-save-on-blur.
 *
 * F-B note: <input type="date"> exchanges YYYY-MM-DD strings; the wire
 * format from pg date columns serializes as ISO with T00:00:00.000Z.
 * dateStrToInput strips the T-component for input binding;
 * inputStrToWire feeds the wire as YYYY-MM-DD (the server's pg date
 * column accepts both, but YYYY-MM-DD is canonical).
 */

import { useState, type ReactNode } from 'react';
import type { AffiliationWithOrg } from '@/lib/types';
import { errorCodeOf, pageWriteText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { DateInput } from '@/components/primitives/DateInput';

/** A failed affiliation save as one sentence. The server answers
 *  primary_affiliation_conflict for any unique clash on the individual's
 *  affiliations, so the sentence does not claim a concurrent edit. */
function affiliationSaveText(e: unknown, contact: SupportContact): string {
  if (errorCodeOf(e) === 'primary_affiliation_conflict') {
    return 'This clashes with an affiliation already on file. Reload the page to see it.';
  }
  return pageWriteText(e, contact);
}

const SAVED_INDICATOR_MS = 1500;

interface FormState {
  role: string;
  title: string;
  start_date: string; // YYYY-MM-DD or empty
  end_date: string;
}

function dateStrToInput(value: string | null): string {
  if (value === null) return '';
  // F-B: strip the T-component from ISO → YYYY-MM-DD for date input.
  return value.split('T')[0] ?? '';
}

function inputStrToWire(value: string): string | null {
  return value === '' ? null : value;
}

function validateForm(s: FormState): string | null {
  if (s.start_date && s.end_date) {
    if (s.end_date < s.start_date) {
      return "The end date can't be before the start date. Change one of them.";
    }
  }
  return null;
}

// ---------- Edit mode (existing affiliation) ----------

export interface AffiliationEditorEditProps {
  affiliation: AffiliationWithOrg;
  onSave: (patch: {
    role: string | null;
    title: string | null;
    start_date: string | null;
    end_date: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}

export function AffiliationEditorEdit({
  affiliation,
  onSave,
  onCancel,
}: AffiliationEditorEditProps): ReactNode {
  const [form, setForm] = useState<FormState>({
    role: affiliation.role ?? '',
    title: affiliation.title ?? '',
    start_date: dateStrToInput(affiliation.start_date),
    end_date: dateStrToInput(affiliation.end_date),
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const contact = useSupportContact();

  async function handleSave(): Promise<void> {
    const err = validateForm(form);
    if (err !== null) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    setSaveState('saving');
    setSaveError(null);
    try {
      await onSave({
        role: form.role.trim() === '' ? null : form.role.trim(),
        title: form.title.trim() === '' ? null : form.title.trim(),
        start_date: inputStrToWire(form.start_date),
        end_date: inputStrToWire(form.end_date),
      });
      // Parent closes the editor on success; we don't reset state here.
    } catch (e) {
      setSaveState('error');
      setSaveError(affiliationSaveText(e, contact));
    }
  }

  return (
    <div className="border border-sage-800/[8%] rounded-md p-3 space-y-3 bg-sand">
      <FormFields
        form={form}
        onChange={setForm}
        validationError={validationError}
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saveState === 'saving'}
          className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:bg-sage-50 rounded-base disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saveState === 'saving'}
          className="min-h-11 px-3 py-1 text-sm bg-sage-800 text-cream rounded-base hover:bg-forest-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {saveState === 'saving' ? 'Saving…' : 'Save'}
        </button>
      </div>
      {saveState === 'error' && (
        <div role="alert" className="text-xs text-red-700" data-testid="affiliation-edit-error">
          ⚠ {saveError ?? 'Save failed'}
        </div>
      )}
    </div>
  );
}

// ---------- Add mode (after org has been picked) ----------

export interface AffiliationEditorAddProps {
  org: { id: string; display_name: string };
  onAdd: (input: {
    role: string | null;
    title: string | null;
    start_date: string | null;
    end_date: string | null;
    is_primary: boolean;
  }) => Promise<void>;
  onCancel: () => void;
}

export function AffiliationEditorAdd({
  org,
  onAdd,
  onCancel,
}: AffiliationEditorAddProps): ReactNode {
  const [form, setForm] = useState<FormState>({
    role: '',
    title: '',
    start_date: '',
    end_date: '',
  });
  const [isPrimary, setIsPrimary] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const contact = useSupportContact();

  async function handleAdd(): Promise<void> {
    const err = validateForm(form);
    if (err !== null) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    setSaveState('saving');
    setSaveError(null);
    try {
      await onAdd({
        role: form.role.trim() === '' ? null : form.role.trim(),
        title: form.title.trim() === '' ? null : form.title.trim(),
        start_date: inputStrToWire(form.start_date),
        end_date: inputStrToWire(form.end_date),
        is_primary: isPrimary,
      });
    } catch (e) {
      setSaveState('error');
      setSaveError(affiliationSaveText(e, contact));
    }
  }

  return (
    <div className="border border-sage-800/[8%] rounded-md p-3 space-y-3 bg-sand">
      <div className="font-medium">{org.display_name}</div>
      <FormFields
        form={form}
        onChange={setForm}
        validationError={validationError}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
          className="min-h-5 min-w-5"
        />
        <span>Set as primary</span>
      </label>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saveState === 'saving'}
          className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:bg-sage-50 rounded-base disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={saveState === 'saving'}
          className="min-h-11 px-3 py-1 text-sm bg-sage-800 text-cream rounded-base hover:bg-forest-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {saveState === 'saving' ? 'Adding…' : 'Add'}
        </button>
      </div>
      {saveState === 'error' && (
        <div role="alert" className="text-xs text-red-700" data-testid="affiliation-add-error">
          ⚠ {saveError ?? 'Add failed'}
        </div>
      )}
    </div>
  );
}

// ---------- Shared form fields ----------

function FormFields({
  form,
  onChange,
  validationError,
}: {
  form: FormState;
  onChange: (next: FormState) => void;
  validationError: string | null;
}): ReactNode {
  return (
    <div className="space-y-2">
      <div>
        <label className="block text-xs text-ink-soft mb-1">Role</label>
        <input
          type="text"
          value={form.role}
          onChange={(e) => onChange({ ...form, role: e.target.value })}
          placeholder="(optional)"
          aria-label="Role"
          className="w-full min-h-11 px-3 py-2 border border-sage-200 rounded-base text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        />
      </div>
      <div>
        <label className="block text-xs text-ink-soft mb-1">Title</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => onChange({ ...form, title: e.target.value })}
          placeholder="(optional)"
          aria-label="Title"
          className="w-full min-h-11 px-3 py-2 border border-sage-200 rounded-base text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-ink-soft mb-1">Start date</label>
          <DateInput
            value={form.start_date}
            onChange={(e) => onChange({ ...form, start_date: e.target.value })}
            aria-label="Start date"
            size="default" className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-ink-soft mb-1">End date</label>
          <DateInput
            value={form.end_date}
            onChange={(e) => onChange({ ...form, end_date: e.target.value })}
            aria-label="End date"
            size="default" className="w-full"
          />
        </div>
      </div>
      {validationError && (
        <div role="alert" className="text-xs text-red-700">
          ⚠ {validationError}
        </div>
      )}
    </div>
  );
}

// SAVED_INDICATOR_MS reserved for future inline indicator in the editor;
// current shape closes on success via onSave/onAdd resolution + parent
// re-render. Keep export in case downstream wants to wire inline status.
void SAVED_INDICATOR_MS;
